import React, { Fragment, FunctionComponent, useCallback, useMemo, useRef, useState } from "react";
import { Button } from "../button/button";
import { IconButton } from "../button/icon-button";
import { Drawer } from "../drawer/drawer";
import { IconCancel, IconMenu } from "../icons";
import { Link } from "../link";
import { VerticalMainNavMenu } from "../nav-menu/vertical-main-nav-menu";
import { Separator } from "../separator/separator";
import { LightDarkVariant, NavMenuItem, NavSubMenuItem } from "../types";
import {
  GlobalHeaderButtonProps,
  GlobalHeaderUtilityHeaderIconLinkProps,
  GlobalHeaderUtilityHeaderLinkProps,
} from "./global-header";
import { CSS_CLASSES } from "./global-header-css-classes";

export interface GlobalHeaderMobileNavMenuProps {
  variant: LightDarkVariant;
  color?: "interactive" | "primary";
  menuItems: NavMenuItem[];
  onMenuItemActivate: (id: string) => void;
  links?: GlobalHeaderUtilityHeaderLinkProps[];
  iconLinks?: GlobalHeaderUtilityHeaderIconLinkProps[];
  ctaButtons: GlobalHeaderButtonProps[];
  isUserSignedIn: boolean;
}

export const GlobalHeaderMobileNavMenu: FunctionComponent<GlobalHeaderMobileNavMenuProps> = ({
  variant,
  color = "interactive",
  menuItems,
  onMenuItemActivate,
  links = [],
  iconLinks = [],
  ctaButtons = [],
  isUserSignedIn = false,
}) => {
  const rootRef = useRef<HTMLButtonElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeMenuItemIndex, setActiveMenuItemIndex] = useState(menuItems?.findIndex((item) => !!item.active));

  const openDrawer = useCallback(() => {
    const newActiveIndex = menuItems?.findIndex((item) => !!item.active);
    if (newActiveIndex !== activeMenuItemIndex) {
      setActiveMenuItemIndex(newActiveIndex);
    }
    setDrawerOpen(true);
  }, [activeMenuItemIndex, menuItems]);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    rootRef.current.scrollIntoView();
  }, []);

  const handleMenuItemActivate = useCallback(
    (_event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>, activeIndex: number[]) => {
      const menuItemActivated = menuItems[activeIndex[0]];
      if (menuItemActivated) {
        if (!menuItemActivated.subMenuItems || menuItemActivated.subMenuItems.length === 0) {
          onMenuItemActivate(menuItemActivated.id);
          closeDrawer();
          menuItemActivated.onClick?.();
        } else {
          setActiveMenuItemIndex(activeIndex[0]);
        }
      } else {
        const activeMenuItem = menuItems.findIndex((item) => item.active);
        setActiveMenuItemIndex(activeMenuItem);
      }
    },
    [closeDrawer, menuItems, onMenuItemActivate]
  );

  const handleSubMenuItemActivate = useCallback(
    (subMenuItem: NavSubMenuItem) => {
      onMenuItemActivate(subMenuItem.id);
      closeDrawer();
      subMenuItem.onClick?.();
    },
    [closeDrawer, onMenuItemActivate]
  );

  const hasMainMenuItems = useMemo(() => menuItems && menuItems.length > 0, [menuItems]);

  const ctaButtonsToRender = useMemo(() => {
    return ctaButtons.filter(({ placementOnMobile }) => placementOnMobile === "mobile-menu");
  }, [ctaButtons]);

  const hasCTAButtons = useMemo(() => ctaButtonsToRender.length > 0, [ctaButtonsToRender]);

  const linksToRender = useMemo(() => {
    return links.filter((l) => !isUserSignedIn || !l.hideWhenSignedIn);
  }, [links, isUserSignedIn]);

  const hasLinks = useMemo(() => linksToRender.length > 0, [linksToRender]);

  const iconLinksToRender = useMemo(() => {
    return iconLinks.filter((i) => (!isUserSignedIn || !i.hideWhenSignedIn) && i.placementOnMobile === "mobile-menu");
  }, [iconLinks, isUserSignedIn]);

  const hasIconLinks = useMemo(() => iconLinksToRender.length > 0, [iconLinksToRender]);

  return hasMainMenuItems || hasCTAButtons || hasIconLinks || hasLinks ? (
    <div className={CSS_CLASSES.GLOBAL_HEADER_MOBILE_NAV_MENU_ROOT}>
      <IconButton
        label="menu"
        icon={<IconMenu />}
        variant="ghost"
        size="small"
        color={variant === "light" ? color : "white"}
        onClick={openDrawer}
      />
      <Drawer
        open={drawerOpen}
        onClose={closeDrawer}
        slideFrom="left"
        extension="full"
        showBackdrop={false}
        makeOthersInert={false}
        slidingContent={
          <div className={CSS_CLASSES.GLOBAL_HEADER_MOBILE_NAV_MENU_CONTENT}>
            {/* Close Button */}
            <IconButton
              label="close"
              icon={<IconCancel />}
              variant="ghost"
              size="medium"
              color={color}
              onClick={closeDrawer}
              className="self-start"
              ref={rootRef}
            />

            {/* Main Mobile Nav Menu */}
            {hasMainMenuItems && (
              <VerticalMainNavMenu
                color={color}
                menuItems={menuItems}
                activeMenuItemIndex={activeMenuItemIndex}
                onMenuItemActivate={handleMenuItemActivate}
                onSubMenuItemActive={handleSubMenuItemActivate}
              />
            )}

            {/* CTA Buttons */}
            {hasCTAButtons && <GlobalHeaderMobileNavCTAButtons ctaButtons={ctaButtonsToRender} />}

            {/* Icon Links */}
            {hasIconLinks && <GlobalHeaderMobileNavIconLinks iconLinks={iconLinksToRender} color={color} />}

            {/* Links */}
            {hasLinks && <GlobalHeaderMobileNavLinks links={linksToRender} color={color} />}
          </div>
        }
      />
    </div>
  ) : null;
};

interface GlobalHeaderMobileNavCTAButtonsProps {
  ctaButtons: GlobalHeaderButtonProps[];
}

const GlobalHeaderMobileNavCTAButtons: FunctionComponent<GlobalHeaderMobileNavCTAButtonsProps> = ({ ctaButtons }) => {
  return (
    <div className={CSS_CLASSES.GLOBAL_HEADER_MOBILE_NAV_CTA_BUTTONS}>
      {ctaButtons.map((props, i) => {
        const passthroughProps = { ...props };
        delete passthroughProps.placementOnMobile;
        delete passthroughProps.hideWhenSignedIn;
        return <Button key={passthroughProps.label || i} width="full" size="medium" {...passthroughProps} />;
      })}
    </div>
  );
};

interface GlobalHeaderMobileNavIconLinksProps {
  iconLinks?: GlobalHeaderUtilityHeaderIconLinkProps[];
  color?: "interactive" | "primary";
}

const GlobalHeaderMobileNavIconLinks: FunctionComponent<GlobalHeaderMobileNavIconLinksProps> = ({
  iconLinks,
  color,
}) => {
  return (
    <div className={CSS_CLASSES.GLOBAL_HEADER_MOBILE_NAV_ICON_LINKS}>
      {iconLinks.map((iconLinkProps, i) => {
        const iconLinkPassthroughProps = { ...iconLinkProps };
        delete iconLinkPassthroughProps.placementOnMobile;
        delete iconLinkPassthroughProps.hideWhenSignedIn;
        return (
          <Fragment key={`${iconLinkProps.children}-${i}`}>
            {i > 0 && (
              <Separator orientation="vertical" gap="lg" className={CSS_CLASSES.GLOBAL_HEADER_UTILITY_LINK_SEPARATOR} />
            )}
            <Link bold={true} color={color} {...iconLinkPassthroughProps} className={iconLinkProps.className} />
          </Fragment>
        );
      })}
    </div>
  );
};

interface GlobalHeaderMobileNavLinksProps {
  links?: GlobalHeaderUtilityHeaderLinkProps[];
  color?: "interactive" | "primary";
}

const GlobalHeaderMobileNavLinks: FunctionComponent<GlobalHeaderMobileNavLinksProps> = ({ links, color }) => {
  return (
    <div className={CSS_CLASSES.GLOBAL_HEADER_MOBILE_NAV_LINKS}>
      {links.map((linkProps, i) => {
        const linkPassthroughProps = { ...linkProps };
        delete linkPassthroughProps.hideWhenSignedIn;
        return (
          <Fragment key={`${linkProps.children}-${i}`}>
            {i > 0 && (
              <Separator orientation="vertical" gap="lg" className={CSS_CLASSES.GLOBAL_HEADER_UTILITY_LINK_SEPARATOR} />
            )}
            <Link bold={true} color={color} {...linkPassthroughProps} className={linkProps.className} />
          </Fragment>
        );
      })}
    </div>
  );
};
